import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Fuel, Truck, Receipt, Plus, Trash2, AlertTriangle, Eye } from 'lucide-react';
import { openReceipt } from './FuelReceiptUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  { value: 'fuel', label: 'Fuel Expense', Icon: Fuel },
  { value: 'truck_lease', label: 'Truck Lease Fee', Icon: Truck },
  { value: 'other', label: 'Other Deduction', Icon: Receipt },
];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString() : '');
const emptyForm = (weekStart) => ({ category: 'fuel', amount: '', txn_date: weekStart || '', description: '', notes: '', truck_unit: '', gallons: '', receipt_ref: '' });

export const usePayrollPeriodDeductions = (payrollId) => {
  const [data, setData] = useState({ applied: [], pending: [], revision: 1 });
  const refresh = useCallback(async () => {
    if (!payrollId) return;
    try {
      const res = await axios.get(`${API}/payrolls/${payrollId}/period-deductions`, { withCredentials: true });
      setData(res.data);
    } catch (_e) { /* surfaced by the parent fetch */ }
  }, [payrollId]);
  useEffect(() => { refresh(); }, [refresh]);
  return { ...data, refresh };
};

const DeductionRow = ({ d, onDelete }) => {
  const cat = CATEGORIES.find((c) => c.value === d.category) || CATEGORIES[2];
  const details = [fmtDate(d.txn_date), d.category === 'other' ? d.description : null, d.truck_unit ? `Unit ${d.truck_unit}` : null,
    d.gallons ? `${d.gallons} gal` : null, d.receipt_ref ? `Receipt ${d.receipt_ref}` : null, d.notes].filter(Boolean);
  return (
    <div data-testid={`period-deduction-row-${d.id}`} className="flex items-center justify-between gap-3 px-3 py-2 border border-slate-200 rounded-md bg-white">
      <div className="flex items-center gap-2 min-w-0">
        <cat.Icon className="w-4 h-4 text-slate-500 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900">{cat.label}</p>
          <p className="text-xs text-slate-500 truncate">{details.join(' · ')}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-sm font-semibold text-slate-900">-{money(d.amount)}</span>
        {d.expense_id && (
          <button data-testid={`view-receipt-${d.id}`} onClick={() => openReceipt(d.expense_id)} className="text-[#002FA7] hover:text-[#00247D]" title="Open original fuel receipt"><Eye className="w-4 h-4" /></button>
        )}
        {d.status === 'pending' ? (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`period-deduction-status-${d.id}`}>Pending revision</span>
        ) : (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid={`period-deduction-status-${d.id}`}>Applied</span>
        )}
        {d.status === 'pending' && onDelete && (
          <button data-testid={`delete-period-deduction-${d.id}`} onClick={() => onDelete(d)} className="text-red-600 hover:text-red-800" title="Remove pending deduction">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export const PeriodDeductionsPanel = ({ driverId, driverName, weekStart, weekEnd, applied, pending, locked, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm(weekStart));
  const [saving, setSaving] = useState(false);
  const mine = (rows) => (rows || []).filter((r) => r.driver_id === driverId);
  const appliedRows = mine(applied);
  const pendingRows = mine(pending);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.txn_date < weekStart || form.txn_date > weekEnd) {
      toast.error(`Date must fall inside this pay period (${fmtDate(weekStart)} – ${fmtDate(weekEnd)})`);
      return;
    }
    setSaving(true);
    try {
      const body = { driver_id: driverId, category: form.category, amount: parseFloat(form.amount), txn_date: form.txn_date,
        description: form.description || null, notes: form.notes || null, truck_unit: form.truck_unit || null,
        gallons: form.gallons ? parseFloat(form.gallons) : null, receipt_ref: form.receipt_ref || null };
      await axios.post(`${API}/period-deductions`, body, { withCredentials: true });
      toast.success('Deduction saved — it is pending until you apply the payroll revision.');
      setForm(emptyForm(weekStart));
      setOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save deduction');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove this pending ${d.category_label || 'deduction'} of ${money(d.amount)}?`)) return;
    try {
      await axios.delete(`${API}/period-deductions/${d.id}`, { withCredentials: true });
      toast.success('Pending deduction removed');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove deduction');
    }
  };

  const subtotal = (rows, cat) => rows.filter((r) => r.category === cat).reduce((a, r) => a + Number(r.amount || 0), 0);

  return (
    <div data-testid={`period-deductions-${driverId}`} className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm font-semibold text-slate-900">Truck lease, fuel &amp; other deductions</p>
          <p className="text-xs text-slate-500">Dated transactions for {driverName} within {fmtDate(weekStart)} – {fmtDate(weekEnd)}. Each fuel stop is saved separately.</p>
        </div>
        {!locked && (
          <button data-testid={`add-period-deduction-${driverId}`} onClick={() => setOpen(!open)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-700">
            <Plus className="w-3.5 h-3.5" /> Add deduction
          </button>
        )}
      </div>
      {open && (
        <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 p-3 bg-white border border-slate-200 rounded-md" data-testid={`period-deduction-form-${driverId}`}>
          <select data-testid="pd-category" value={form.category} onChange={set('category')} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm">
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <input data-testid="pd-amount" type="number" step="0.01" min="0.01" required placeholder="Amount" value={form.amount} onChange={set('amount')} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
          <input data-testid="pd-date" type="date" required min={weekStart} max={weekEnd} value={form.txn_date} onChange={set('txn_date')} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
          {form.category === 'other' && <input data-testid="pd-description" required placeholder="Description" value={form.description} onChange={set('description')} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm" />}
          {form.category === 'fuel' && (<>
            <input data-testid="pd-truck-unit" placeholder="Truck / unit #" value={form.truck_unit} onChange={set('truck_unit')} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
            <input data-testid="pd-gallons" type="number" step="0.01" min="0" placeholder="Gallons" value={form.gallons} onChange={set('gallons')} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
            <input data-testid="pd-receipt" placeholder="Receipt # / reference" value={form.receipt_ref} onChange={set('receipt_ref')} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
          </>)}
          <input data-testid="pd-notes" placeholder="Notes (optional)" value={form.notes} onChange={set('notes')} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm col-span-2" />
          <div className="col-span-2 md:col-span-4 flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs rounded-md border border-slate-300">Cancel</button>
            <button data-testid="pd-save" type="submit" disabled={saving} className="px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50">{saving ? 'Saving…' : 'Save deduction'}</button>
          </div>
        </form>
      )}
      {appliedRows.length === 0 && pendingRows.length === 0 && <p className="text-xs text-slate-500" data-testid={`period-deductions-empty-${driverId}`}>No truck lease, fuel or other deductions this period.</p>}
      <div className="space-y-1.5">
        {appliedRows.map((d) => <DeductionRow key={d.id} d={d} />)}
        {pendingRows.map((d) => <DeductionRow key={d.id} d={d} onDelete={remove} />)}
      </div>
      {(appliedRows.length + pendingRows.length) > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600" data-testid={`period-deduction-subtotals-${driverId}`}>
          {CATEGORIES.map((c) => { const v = subtotal(appliedRows, c.value); return v ? <span key={c.value}>{c.label} applied: <b>{money(v)}</b></span> : null; })}
          {pendingRows.length > 0 && <span className="text-amber-700">Pending (not yet applied): <b>{money(pendingRows.reduce((a, r) => a + Number(r.amount || 0), 0))}</b></span>}
        </div>
      )}
    </div>
  );
};

export const PendingRevisionBanner = ({ pending, revision, onApply, applying, locked }) => {
  if (!pending?.length) return null;
  const total = pending.reduce((a, r) => a + Number(r.amount || 0), 0);
  return (
    <div data-testid="pending-revision-banner" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-amber-900">{pending.length} deduction{pending.length > 1 ? 's' : ''} ({money(total)}) saved but not yet applied</p>
          <p className="text-xs text-amber-800 mt-0.5">This finalized payroll (revision {revision || 1}) is unchanged until you apply a revision. Applying rebuilds the run from current loads and deductions, re-applies advances/escrow, and records the revision in the audit log.</p>
        </div>
      </div>
      {!locked && (
        <button data-testid="apply-revision-button" onClick={onApply} disabled={applying}
          className="shrink-0 px-4 py-2 text-sm font-semibold rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">
          {applying ? 'Applying…' : `Apply revision ${(revision || 1) + 1}`}
        </button>
      )}
    </div>
  );
};
